import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { branchService } from '@/services/branchService'
import { reportService } from '@/services/reportService'
import { BranchEntity } from '@/api/types'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'
import { formatCurrency } from '@/lib/format'
import {
  visibilityFromColumnIdsParam,
  countVisibleColumns,
} from '@/components/shared/TableColumnVisibilityMenu'

const CUSTOMER_LEDGER_PRINT_COLUMNS = {
  date: true,
  customer: true,
  voucher: true,
  description: true,
  project: true,
  costCenter: true,
  costLocation: true,
  ref: true,
  notes: true,
  debit: true,
  credit: true,
  balance: true,
}

interface CustomerLedgerEntry {
  account: string | null
  customer: string | null
  costCenter: string | null
  costLocation: string | null
  vendor: string | null
  accountCode: string | null
  description: string
  voucher: number
  type: string | null
  debit: number
  credit: number
  balance: number
  project: string | null
  ref: string | null
  department: string | null
  notes: string | null
  createdDate: string
  branchName: string | null
}

const formatDate = (value?: string) => {
  if (!value) return '-'
  try {
    const date = new Date(value)
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return value
  }
}

const formatAmount = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '0.00'
  return formatCurrency(Number(value))
}

const buildAddress = (entity?: Partial<BranchEntity> | null) => {
  if (!entity) return ''
  const source = entity as Record<string, any>
  const parts = [
    source.address,
    source.addressEn,
    source.addressAr,
    source.street,
    source.citySubdivision,
    source.citySubdivisionAr,
    source.cityName,
    source.cityNameAr,
    source.city,
    source.countryName,
    source.countryNameAr,
    source.countryAr,
    source.zipCode,
    source.postalCode,
  ]
  return parts.filter((part) => typeof part === 'string' && part.trim().length > 0).join(', ')
}

export default function CustomerLedgerReportPrint(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // const { user } = useAuthStore() // Removed unused

  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''
  const branchIdParam = searchParams.get('branchId')
  const customerIdParam = searchParams.get('customerId')
  const colVis = visibilityFromColumnIdsParam(searchParams.get('cols'), CUSTOMER_LEDGER_PRINT_COLUMNS)
  const visibleCount = countVisibleColumns(colVis)
  const labelSpan = ['date', 'customer', 'voucher', 'description', 'project', 'costCenter', 'costLocation', 'ref', 'notes'].filter((k) => colVis[k]).length

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [ledgerData, setLedgerData] = useState<CustomerLedgerEntry[]>([])
  const [ledgerTotals, setLedgerTotals] = useState({
    totalDebit: 0,
    totalCredit: 0,
    overallBalance: 0,
  })
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [, setBranchCityName] = useState<string>('')

  useEffect(() => {
    if (!fromDate || !toDate || !branchIdParam) {
      setError('Missing required parameters')
      setLoading(false)
      return
    }

    const loadReport = async () => {
      try {
        setLoading(true)
        setError(null)

        const branchId = Number(branchIdParam)
        const customerId = customerIdParam ? Number(customerIdParam) : 0

        // Convert dates to ISO format
        const fromDateTime = fromDate.includes('T') ? fromDate : `${fromDate}T00:00:00`
        const toDateTime = toDate.includes('T') ? toDate : `${toDate}T23:59:59`

        const [reportData, branchData] = await Promise.all([
          reportService.getCustomerLedgerReport({
            fromDate: fromDateTime,
            toDate: toDateTime,
            branchId,
            customerId,
            pageNumber: 1,
            pageSize: 10000, // Large page size to get all data for print
          }),
          branchService.getBranchById(branchId).catch(() => null),
        ])

        setLedgerData(reportData?.data || [])
        setLedgerTotals({
          totalDebit: reportData?.totalDebit ?? 0,
          totalCredit: reportData?.totalCredit ?? 0,
          overallBalance: reportData?.overallBalance ?? 0,
        })
        setBranch(branchData)

        if (branchData?.country && branchData?.city) {
          try {
            const branchCities = await branchService.getCityLookups(branchData.country)
            const branchCity = branchCities.find((c: any) => c.id === branchData.city)
            if (branchCity) setBranchCityName(branchCity.lookupNameEn || '')
          } catch (err) {
            // Error handled silently
          }
        }
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Unable to load report'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    loadReport()
  }, [fromDate, toDate, branchIdParam, customerIdParam])

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing report for print...</p>
        </div>
      </div>
    )

  if (error || !ledgerData)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <p className="mb-4 text-lg font-semibold text-destructive">{error || 'Report not found'}</p>
        <Button onClick={() => navigate('/reports/customer-ledger')}>Back to Report</Button>
      </div>
    )

  return (
    <div className="ledger-print-root p-6 bg-gray-50 print:bg-white">
      {/* Print styles embedded */}
      <style>{`
        @media print {
          @page { 
            size: A4 landscape; 
            margin: 8mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            height: auto;
            overflow: hidden;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important }
          .ledger-print-root {
            margin: 0;
            padding: 0;
            height: auto;
          }
          .ledger-wrapper {
            page-break-after: avoid;
            page-break-inside: avoid;
          }
          table { 
            page-break-inside: auto;
          }
          tr { 
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        }

        .ledger-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937 }
        .ledger-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: 0.5px solid #ddd }
        .top-brand { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 4px }
        .company-title { font-weight:700; font-size:14px; letter-spacing:0.2px; text-align:center }
        .ledger-main-title { text-align:center; font-size:16px; font-weight:800; margin-top:4px }
        .divider-strong { height:3px; background:#0A1E60; margin:6px 0; }

        /* Date range filter */
        .date-filter { display:flex; justify-content:space-between; background:#E5E7EB; padding:8px; margin:8px 0; border-radius:4px }
        .date-item { flex:1; text-align:center }

        /* Ledger table */
        .ledger-table { width:100%; border-collapse:collapse; font-size:9px; margin-top:4px }
        .ledger-table th, .ledger-table td { border:none; border-bottom:1px solid #E5E7EB; padding:3px; }
        .ledger-table thead th { background:#F5F5F5; font-weight:700; font-size:9px }
        .ledger-table tbody tr td { vertical-align:middle; font-size:9px }
        .ledger-total-row { background:#F5F5F5; font-weight:800 }

        /* View mode - make it compact like print */
        @media screen {
          .ledger-wrapper { max-width: 1100px; padding: 12px }
          .top-brand { margin-bottom: 6px }
          .company-title { font-size: 16px }
          .ledger-main-title { font-size: 18px; margin-top: 6px }
          .divider-strong { margin: 8px 0 }
          .ledger-table { font-size: 10px; margin-top: 6px }
          .ledger-table th, .ledger-table td { padding: 4px }
          .ledger-table thead th { font-size: 10px }
          .ledger-table tbody tr td { font-size: 10px }
          .date-filter { padding: 10px; margin: 10px 0 }
        }

        /* Arabic Right-to-left fixes */
        .rtl { direction: rtl; unicode-bidi: embed }
      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/reports/customer-ledger')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print Report
        </Button>
      </div>

      <div className="ledger-wrapper">
        {/* Header */}
        <div className="top-brand">
          <div style={{ width: '30%', fontSize: 11 }} className="print:text-[10px]">
            <div><strong>{branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}>
              <strong>Address:</strong> {buildAddress(branch) || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Email:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Phone:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>CR No:</strong> {branch?.crNo || '-'}
            </div>
          </div>

          <div style={{ width: '40%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <img src={getLogoPath()} alt="Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} className="print:h-10" />
          </div>

          <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }} className="print:text-[10px] rtl">
            <div><strong>{branch?.nameAr || branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}>
              <strong>العنوان:</strong> {branch?.addressAr || buildAddress(branch) || '--'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>البريد الإلكتروني:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>هاتف:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>رقم تسجيل الشركة:</strong> {branch?.crNo || '-'}
            </div>
          </div>
        </div>

        <div className="divider-strong print:my-1" style={{ backgroundColor: '#0A1E60', height: '3px', margin: '8px 0' }} />

        {/* Report Title */}
        <div className="print:mb-2" style={{ textAlign: 'center', marginBottom: 6 }}>
          <div className="ledger-main-title print:text-base" style={{ fontSize: 18, fontWeight: 700 }}>Customer Ledger</div>
        </div>

        {/* Date Range Filter */}
        <div className="date-filter print:my-2" style={{ backgroundColor: '#E5E7EB', padding: '8px', margin: '8px 0', borderRadius: '4px' }}>
          <div className="date-item" style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 10, marginBottom: 2 }}>▼FROM DATE</div>
            <div style={{ fontSize: 11 }}>{formatDate(fromDate)}</div>
          </div>
          <div className="date-item" style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 10, marginBottom: 2 }}>▼TO DATE</div>
            <div style={{ fontSize: 11 }}>{formatDate(toDate)}</div>
          </div>
        </div>

        {/* Ledger Table */}
        <table className="ledger-table">
          <thead>
            <tr>
              {colVis.date && <th>DATE</th>}
              {colVis.customer && <th>CUSTOMER</th>}
              {colVis.voucher && <th>VOUCHER</th>}
              {colVis.description && <th>DESCRIPTION</th>}
              {colVis.project && <th>PROJECT</th>}
              {colVis.costCenter && <th>COST CENTER</th>}
              {colVis.costLocation && <th>COST LOCATION</th>}
              {colVis.ref && <th>REFERENCE</th>}
              {colVis.notes && <th>NOTES</th>}
              {colVis.debit && <th style={{ textAlign: 'right' }}>DEBIT</th>}
              {colVis.credit && <th style={{ textAlign: 'right' }}>CREDIT</th>}
              {colVis.balance && <th style={{ textAlign: 'right' }}>BALANCE</th>}
            </tr>
          </thead>
          <tbody>
            {ledgerData.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, visibleCount)} style={{ textAlign: 'center', padding: 16 }}>
                  No ledger entries available
                </td>
              </tr>
            ) : (
              ledgerData.map((entry, idx) => (
                <tr key={idx}>
                  {colVis.date && <td>{formatDate(entry.createdDate)}</td>}
                  {colVis.customer && <td>{entry.customer || '-'}</td>}
                  {colVis.voucher && <td>{entry.voucher || '-'}</td>}
                  {colVis.description && <td>{entry.description || '-'}</td>}
                  {colVis.project && <td>{entry.project || '-'}</td>}
                  {colVis.costCenter && <td>{entry.costCenter || '-'}</td>}
                  {colVis.costLocation && <td>{entry.costLocation || '-'}</td>}
                  {colVis.ref && <td>{entry.ref || '-'}</td>}
                  {colVis.notes && <td>{entry.notes || '-'}</td>}
                  {colVis.debit && <td style={{ textAlign: 'right' }}>{formatAmount(entry.debit)}</td>}
                  {colVis.credit && <td style={{ textAlign: 'right' }}>{formatAmount(entry.credit)}</td>}
                  {colVis.balance && <td style={{ textAlign: 'right' }}>{formatAmount(entry.balance)}</td>}
                </tr>
              ))
            )}

            <tr className="ledger-total-row">
              {labelSpan > 0 && (
                <td colSpan={labelSpan} style={{ textAlign: 'right', fontWeight: 700 }}>
                  TOTAL
                </td>
              )}
              {colVis.debit && <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(ledgerTotals.totalDebit)}</td>}
              {colVis.credit && <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(ledgerTotals.totalCredit)}</td>}
              {colVis.balance && <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(ledgerTotals.overallBalance)}</td>}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

